<template>
  <div :class="getPanelClass" :style="{ background: bg }">
    <div v-if="hasTitle" :class="['mb-6px flex items-center justify-between', headerClass]">
      <div class="gap-6px flex items-center">
        <slot name="titleBefore"></slot>
        <slot name="title">
          <div class="text-16px font-500">{{ t(title) }}</div>
          <div v-if="subTitle" class="text-[0.875em] text-[#7A8494]">{{ t(subTitle) }}</div>
        </slot>
        <slot name="headerLeft"></slot>
      </div>
      <div class="gap-6px flex items-center">
        <slot name="headerRight"></slot>
        <div v-if="canCollapse" class="flex" @click="collapsed = !collapsed">
          <span class="ml-4px text-[#01a887]">
            {{ collapsed ? t('g.26080002') : t('g.26080003') }}
          </span>
          <MIcon
            prefix="g"
            color="#01a887"
            :size="20"
            icon="arrow-down"
            :class="'leading-none ' + (collapsed ? '' : 'rotate-180deg transform')"
          />
        </div>
      </div>
    </div>
    <div :class="['flex-1', contentClass]">
      <slot></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, useSlots } from 'vue';
  import { useDesign } from '@mts/hooks/web/useDesign';
  import { t } from '@mts/hooks/web/useI18n';

  defineOptions({
    name: 'BasePanel',
  });

  const props = withDefaults(
    defineProps<{
      /** 标题 */
      title?: string;
      subTitle?: string;
      hideTitle?: boolean;
      small?: boolean;
      inset?: boolean;
      canCollapse?: boolean;
      headerClass?: string;
      contentClass?: string;
      bg?: string;
    }>(),
    { title: '' },
  );

  const collapsed = defineModel<boolean>('collapsed');

  const { prefixCls } = useDesign('base-panel');
  const getPanelClass = computed(() => {
    return [
      prefixCls,
      {
        [`${prefixCls}--small`]: props.small,
        [`${prefixCls}--inset`]: props.inset,
      },
    ];
  });

  const slots = useSlots();
  const hasTitle = computed(
    () =>
      !props.hideTitle &&
      !!(props.title || props.subTitle || slots?.title || slots?.headerLeft || slots?.headerRight),
  );
</script>

<style lang="less">
  @prefix-cls: ~'@{namespace}-base-panel';

  .@{prefix-cls} {
    display: flex;
    position: relative;
    flex-direction: column;
    margin: 8px;
    padding: 12px;
    border-radius: @border-radius;
    background-color: #fff;
    background-repeat: no-repeat;
    background-size: cover;

    &--small {
      margin: 4px;
    }

    &--inset {
      margin: 0;
    }
  }
</style>
